export interface Tomo {
  emoji: string
  num: string
  title: string
  /** Resultado práctico que ayuda a conseguir — encabeza la tarjeta. */
  outcome: string
  desc: string
}
export interface Pillar {
  title: string
  desc: string
}
export interface Bono {
  num: string
  title: string
  desc: string
}
export interface UseCase {
  role: string
  desc: string
}
export interface Faq {
  q: string
  a: string
}

/** Precio único vigente de la landing. */
/**
 * Formato monetario único de la landing: siempre "AR$ 00.000".
 * Nunca añadir "ARS" cuando ya se antepone "AR$".
 */
export const PRICE = {
  previous: 'AR$ 29.990',
  current: 'AR$ 12.990',
  note: 'Pago único',
} as const

export const PRODUCT_FACTS: string[] = [
  '6 Tomos',
  '5 Bonos',
  '350+ páginas',
  'Acceso inmediato',
]

export const TOMOS: Tomo[] = [
  {
    emoji: '📘',
    num: 'Tomo 1',
    title: 'El Estudio Digital',
    outcome: 'Ordenar la base de todo tu sistema de Instagram',
    desc: 'Perfil, bio, SEO local, destacadas y link de reserva.',
  },
  {
    emoji: '📗',
    num: 'Tomo 2',
    title: 'Arquitectura Visual',
    outcome: 'Que tu perfil transmita el profesionalismo que ya tenés',
    desc: 'Imagen de marca, fotografía con celular, identidad visual y feed premium.',
  },
  {
    emoji: '📙',
    num: 'Tomo 3',
    title: 'Conexión Consciente',
    outcome: 'Generar conversaciones y oportunidades reales cada semana',
    desc: 'Stories, conexión diaria y venta invisible.',
  },
  {
    emoji: '📕',
    num: 'Tomo 4',
    title: 'El Método en Movimiento',
    outcome: 'Llegar a personas nuevas que todavía no te conocen',
    desc: 'Reels, alcance, autoridad y posicionamiento.',
  },
  {
    emoji: '📓',
    num: 'Tomo 5',
    title: 'Ventas con Propósito',
    outcome: 'Convertir consultas en alumnos y clientes',
    desc: 'Ventas por DM, objeciones y cierre de inscripciones.',
  },
  {
    emoji: '📔',
    num: 'Tomo 6',
    title: 'El Calendario Zen',
    outcome: 'Dejar de improvisar qué publicar',
    desc: 'Planificación mensual, contenido y batching.',
  },
]

/** Las cinco piezas que convierten el pack en un sistema comercial. */
export const SYSTEM_PILLARS: Pillar[] = [
  { title: 'Contenido', desc: 'Qué publicar y por qué, sin partir de cero cada semana.' },
  { title: 'Estrategia', desc: 'Cada pieza de tu perfil con una función clara dentro del sistema.' },
  { title: 'Conversión', desc: 'Contenido pensado para generar consultas, no solo likes.' },
  { title: 'Ventas', desc: 'Un protocolo de respuesta y cierre por DM y WhatsApp.' },
  { title: 'Planificación', desc: 'Un mes de contenido resuelto en una sola mañana.' },
]

export const TOMO_FEATURES: string[] = [
  'Estrategia lista para implementar',
  'Ejemplos del mundo wellness',
  'Scripts y textos listos',
  'Checklists',
  'Ejercicios prácticos',
  'Plantillas y sistemas',
  'Casos locales',
  'Orientado a convertir seguidores en alumnos/clientes',
]

export const BONOS: Bono[] = [
  {
    num: '01',
    title: 'Scripts de Retiros y Talleres',
    desc: 'El sistema para llenar eventos presenciales y virtuales con comunicación persuasiva.',
  },
  {
    num: '02',
    title: 'WhatsApp Business para Bienestar',
    desc: 'Configuración estratégica y guiones de cierre para convertir consultas en inscripciones.',
  },
  {
    num: '03',
    title: '50 Ganchos Wellness',
    desc: 'Hooks para Reels, Posts y Stories clasificados por objetivo y sub-nicho.',
  },
  {
    num: '04',
    title: 'Iluminación y Audio Low-Cost',
    desc: 'Cómo lograr videos profesionales con celular sin invertir en equipos caros.',
  },
  {
    num: '05',
    title: 'Vencer el Miedo a la Cámara',
    desc: 'Método para comunicar con confianza y autoridad digital frente al celular.',
  },
]

export const BONOS_TOTAL = 'AR$ 43.000'

/**
 * Ejemplos de aplicación del sistema. NO son testimonios ni experiencias de
 * clientes: se presentan explícitamente como casos de uso ilustrativos.
 */
export const USE_CASES: UseCase[] = [
  {
    role: 'Profesora de Pilates',
    desc: 'Ordena su contenido, mejora cómo comunica sus clases y usa Stories para generar nuevas consultas.',
  },
  {
    role: 'Profesora de Yoga',
    desc: 'Deja de depender solamente del boca en boca y empieza a usar Reels y contenido estratégico para llegar a nuevos alumnos.',
  },
  {
    role: 'Coach / profesional del bienestar',
    desc: 'Organiza su propuesta, crea contenido con intención y mejora la forma de responder y cerrar consultas.',
  },
]

export const PAIN_POINTS: string[] = [
  'Publicás con constancia pero las consultas no llegan',
  'Cada semana empezás de cero pensando qué subir',
  'Tus alumnos nuevos dependen casi siempre del boca en boca',
  'Tu trabajo es excelente, pero tu Instagram no lo comunica',
  'Perdés horas creando contenido sin una estrategia detrás',
  'Respondés una consulta con el precio y la conversación se corta',
]

export const FAQS: Faq[] = [
  {
    q: '¿Sirve aunque tenga pocos seguidores?',
    a: 'Sí. El sistema está diseñado exactamente para eso. El Tomo 1 optimiza el perfil para convertir visitas en consultas independientemente del número de seguidores. No necesitás miles de seguidores: un perfil pequeño pero bien orientado también puede generar consultas.',
  },
  {
    q: '¿Cómo recibo el material?',
    a: '100% digital. Acceso en menos de 5 minutos después del pago. Recibirás un email con el link de descarga. Los archivos son PDF de alta calidad, listos para leer en celular, tablet o computadora.',
  },
  {
    q: '¿Necesito saber de diseño o marketing?',
    a: 'No. Todo está explicado paso a paso en lenguaje simple y directo. No necesitás conocimientos previos de marketing ni de diseño. Si sabés usar Instagram, podés implementar este sistema.',
  },
  {
    q: '¿Tiene garantía real?',
    a: 'Sí. 7 días completos. Si no estás satisfecho/a, escribís a soporte@creativodigital.shop y te devolvemos el 100% sin preguntas ni formularios complicados.',
  },
]
